import { useState, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Typography, useTheme } from '../../base';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import { Directions } from '../../common';
import {
    ActionButton,
    NavButton,
    NavigationControls,
    PDFContainer,
    OverlayControls,
    ViewerContainer,
} from './PDFViewer.styles';
import { useTranslation } from 'react-i18next';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface Props {
    url: string;
    disableActionButtons?: boolean;
    useOriginalSize?: boolean;
    scrollPages?: boolean;
    showFullscreenButton?: boolean;
    onFullscreen?: () => void;
}

const PDFViewer = (props: Props) => {
    const {
        url = '',
        disableActionButtons = false,
        useOriginalSize = false,
        scrollPages = false,
        showFullscreenButton = false,
        onFullscreen,
    } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const [numPages, setNumPages] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(true);
    const [scale, setScale] = useState<number>(1);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const updateScale = () => {
            if (containerRef.current && !useOriginalSize) {
                const { width } = containerRef.current.getBoundingClientRect();
                setScale(width / 400); // TODO: figure this out
            } else {
                setScale(1); // Use original size
            }
        };

        updateScale();
        window.addEventListener('resize', updateScale);
        return () => window.removeEventListener('resize', updateScale);
    }, [useOriginalSize]);

    // Reset scroll position when document loads
    useEffect(() => {
        if (!loading && containerRef.current) {
            containerRef.current.scrollTop = 0;
        }
    }, [loading]);

    function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
        setNumPages(numPages);
        setLoading(false);
    }

    const handlePrevPage = () => {
        setPageNumber((page) => Math.max(page - 1, 1));
    };

    const handleNextPage = () => {
        setPageNumber((page) => Math.min(page + 1, numPages));
    };

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = url;
        link.download = 'document.pdf';
        link.click();
    };

    const handlePrint = () => {
        const printWindow = window.open(url);
        printWindow?.print();
    };

    return (
        <ViewerContainer ref={containerRef}>
            <PDFContainer
                useOriginalSize={useOriginalSize}
                scrollPages={scrollPages}
            >
                <Document
                    file={url}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={<Typography>Loading PDF...</Typography>}
                    error={
                        <Typography color="error">
                            {t('components.errorPDF')}
                        </Typography>
                    }
                >
                    {scrollPages ? (
                        // Render all pages in scroll mode
                        Array.from(new Array(numPages), (_, index) => (
                            <Page
                                key={`page_${index + 1}`}
                                pageNumber={index + 1}
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                                scale={scale}
                            />
                        ))
                    ) : (
                        // Render single page in pagination mode
                        <Page
                            pageNumber={pageNumber}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                            scale={scale}
                        />
                    )}
                </Document>

                {!loading && !scrollPages && numPages > 1 && (
                    <NavigationControls>
                        {pageNumber > 1 && (
                            <NavButton
                                onClick={handlePrevPage}
                                aria-label={t('components.previousPage')}
                                direction={
                                    theme.direction === Directions.rtl
                                        ? 'right'
                                        : 'left'
                                }
                            >
                                {theme.direction === Directions.rtl ? (
                                    <ChevronRightIcon />
                                ) : (
                                    <ChevronLeftIcon />
                                )}
                            </NavButton>
                        )}
                        {pageNumber < numPages && (
                            <NavButton
                                onClick={handleNextPage}
                                aria-label={t('components.nextPage')}
                                direction={
                                    theme.direction === Directions.rtl
                                        ? 'left'
                                        : 'right'
                                }
                            >
                                {theme.direction === Directions.rtl ? (
                                    <ChevronLeftIcon />
                                ) : (
                                    <ChevronRightIcon />
                                )}
                            </NavButton>
                        )}
                    </NavigationControls>
                )}
            </PDFContainer>
            {!disableActionButtons && (
                <OverlayControls>
                    {showFullscreenButton && onFullscreen && (
                        <ActionButton
                            onClick={onFullscreen}
                            aria-label={t('components.viewFullscreen')}
                        >
                            <SearchIcon />
                        </ActionButton>
                    )}
                    <ActionButton
                        onClick={handleDownload}
                        aria-label={t('components.download')}
                    >
                        <DownloadIcon />
                    </ActionButton>
                    <ActionButton
                        onClick={handlePrint}
                        aria-label={t('components.print')}
                    >
                        <PrintIcon />
                    </ActionButton>
                </OverlayControls>
            )}
        </ViewerContainer>
    );
};

export default PDFViewer;
